import { getPlugin } from '@/utils/section/get-plugin'
import { useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'

interface Props {
  data: any
  setStartDate?: (date: Date | null) => void
  setEndDate?: (date: Date | null) => void
  setFacets: (facets: string[]) => void
  setIsOpenToday?: (isOpenToday: boolean) => void
  setSearch?: (search: string) => void
  setFilterUrlForBackend?: (filterUrlForBackend: string) => void
  facets: string[]
  startDate?: Date | null
  endDate?: Date | null
  search?: string
  isOpenToday?: boolean
  linkToPdfBackend?: string
  filterUrlForBackend?: string
}

const useSanityFilterSync = ({
  data,
  setEndDate,
  setFacets,
  setSearch,
  setStartDate,
  setFilterUrlForBackend,
  setIsOpenToday,
  facets,
  startDate,
  endDate,
  search,
  isOpenToday,
  linkToPdfBackend,
  filterUrlForBackend
}: Props) => {
  const searchParams = useSearchParams()
  const isInitialMount = useRef(true)
  const plugin = getPlugin(data)

  useEffect(() => {
    const initialDataFacets = plugin?.initialFilterFacet || []
    const urlFacetsRaw = searchParams.get('facets')
    const urlFacets = urlFacetsRaw ? urlFacetsRaw.split(',') : []

    if (setIsOpenToday && plugin?.hasShowOpenClosed) {
      const isOpenTodayInUrl = searchParams.get('isOpenToday') === 'true'
      setIsOpenToday(isOpenTodayInUrl)
    }

    const allFacets = [...initialDataFacets, ...urlFacets]
      .map((facet) => facet.trim())
      .filter(Boolean)

    const initialFacets = Array.from(new Set(allFacets))

    setFacets(initialFacets)
    const urlStartDate = searchParams.get('startDate')
    const urlEndDate = searchParams.get('endDate')
    const urlSearch = searchParams.get('search')

    if (setStartDate) {
      if (urlStartDate) {
        setStartDate(new Date(urlStartDate))
      } else {
        setStartDate(null)
      }
    }

    if (setEndDate) {
      if (urlEndDate) {
        setEndDate(new Date(urlEndDate))
      } else {
        setEndDate(null)
      }
    }

    if (urlSearch && setSearch) {
      setSearch(urlSearch)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    const url = new URL(window.location.href)

    if (facets.length > 0) {
      url.searchParams.set('facets', facets.join(','))
    } else {
      url.searchParams.delete('facets')
    }

    if (plugin?.hasShowOpenClosed) {
      if (isOpenToday) {
        url.searchParams.set('isOpenToday', 'true')
      } else {
        url.searchParams.delete('isOpenToday')
      }
    }

    if (startDate) {
      url.searchParams.set('startDate', startDate.toISOString())
    } else {
      url.searchParams.delete('startDate')
    }
    if (endDate) {
      url.searchParams.set('endDate', endDate.toISOString())
    } else {
      url.searchParams.delete('endDate')
    }
    if (search) {
      url.searchParams.set('search', search)
    } else {
      url.searchParams.delete('search')
    }

    window.history.replaceState({}, '', url)

    if (setFilterUrlForBackend && linkToPdfBackend) {
      setFilterUrlForBackend?.(
        linkToPdfBackend
          .replace(/---facets---/g, facets.join(','))
          .replace(/---todayOpen---/g, isOpenToday ? '1' : '0')
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    facets,
    startDate,
    endDate,
    search,
    isOpenToday,
    linkToPdfBackend,
    filterUrlForBackend,
    setFilterUrlForBackend
  ])
}

export default useSanityFilterSync
